import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../services/api';
import { BackButton } from '../components/BackButton';
import { AddWorkModal } from '../components/AddWorkModal';
import { useVesselWorkTypes, workTypeEmoji, type WorkTypeMaster } from '../hooks/useVesselWorkTypeOptions';
import { hasFeature } from '../utils/featureFlags';
import { useAlcoholWorkflowSettings, notifiedPartiesLabel } from '../hooks/useAlcoholWorkflowSettings';
import { judgeAlcohol } from '../utils/alcoholThresholds';
import { triggerSync } from '../utils/autoSync';
import {
  getEdits, getEditsForMonth, enqueueEdit, removeEdit, removeEditsBySession,
  isLocalId, newLocalId, isOfflineError, flushEdits, type PendingEdit,
} from '../utils/pendingEdits';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import type { AuthUser } from '../hooks/useAuth';
import { BottomNav } from '../components/BottomNav';

interface Props { user: AuthUser; }

type RecordSource = 'gps_auto' | 'admin_input' | 'crew_edit' | 'manual';

interface SessionRecord {
  id: string;
  vessel_id?: string;
  /** true = 臨時業務だけを保持する入れ物。通常勤怠としては表示・集計しない */
  isActivityOnly?: boolean;
  /** true = 管理者の操作（船員デモ・配乗の無い管理者の打刻）。一覧には出すが合計には入れない */
  adminOperated?: boolean;
  clockIn?: string; clockOut?: string;
  workMin?: number; overMin?: number; breakMin?: number;
  status?: 'completed' | 'working' | 'absent';
  recordSource?: RecordSource;
  alcoholValue?: number;
  workType?: string;
  /** 端末に貯めてまだ送っていない修正・追加を含む行 */
  pending?: boolean;
}

interface DayRecord {
  date: string; weekday: string; sessions: SessionRecord[];
}

interface ActivityLogEntry {
  id: string; work_session_id: string;
  name: string; emoji: string; color: string;
  started_at: string; ended_at: string; duration_minutes: number;
  /** 労働区分（機能フラグ labor_categories）。扱いはサーバーが区分マスタから付けて返す */
  labor_category?: string | null; labor_category_name?: string | null; labor_category_color?: string | null;
  counts_as_work?: number; counts_toward_limit?: number; permits_over_limit?: number; counts_as_rest?: number;
  reason?: string | null;
  /** 所属セッションの勤務日。セッションが消えても日付で表示できるようにサーバーから受け取る */
  target_date?: string;
  vessel_id?: string;
}

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

const SOURCE_LABEL: Record<string, string> = {
  gps_auto: 'GPS自動', admin_input: '管理者修正', crew_edit: '自己修正', manual: '手動入力',
};

function getDaysInMonth(year: number, month: number): string[] {
  const days: string[] = [];
  const count = new Date(year, month, 0).getDate();
  for (let d = 1; d <= count; d++) {
    days.push(year + '-' + String(month).padStart(2, '0') + '-' + String(d).padStart(2, '0'));
  }
  return days;
}

function fmtMin(min?: number): string {
  if (!min) return '0h';
  const h = Math.floor(min / 60), m = min % 60;
  return m > 0 ? h + 'h' + m + 'm' : h + 'h';
}

/** ISO文字列からJSTの日付(YYYY-MM-DD)を求める */
function jstDateOf(isoStr?: string): string {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '';
  return new Date(d.getTime() + 9 * 3600000).toISOString().slice(0, 10);
}

function fmtTime(isoStr?: string): string {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo', hour12: false });
}

// ─── 業務の種別バッジ ────────────────────────────────────────────────
// unified_work_types が有効な会社では「通常/臨時」の区分を出さず、作業名だけを見せる。
function WorkTypeBadge({ workType, types }: { workType?: string; types: WorkTypeMaster[] }) {
  const unified = hasFeature('unified_work_types');
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 5 }}>
      {!unified && (
        <span style={{ fontSize: 11, fontWeight: 700, color: '#0057d9', background: '#eff6ff', padding: '2px 8px', borderRadius: 99, border: '1px solid #bfdbfe' }}>🕐 通常業務</span>
      )}
      {workType
        ? <span style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>{workTypeEmoji(types, workType)} {workType}</span>
        : <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>作業種別なし</span>}
    </div>
  );
}

// ─── 臨時業務の行（通常業務と同じ履歴の並びに表示する） ────────────────
// unified_work_types が有効な会社では、色・枠も通常業務と同じにして区別を無くす。
/** 労働区分の3本のどれに入るか（労働／別枠／労働外）。区分の無い記録は「労働」 */
function bucketOfLog(l: { counts_as_work?: number; counts_toward_limit?: number; permits_over_limit?: number }): 'work' | 'exempt' | 'nonwork' {
  if (l.counts_as_work === 0) return 'nonwork';
  if (l.counts_toward_limit === 0 || l.permits_over_limit === 1) return 'exempt';
  return 'work';
}

function ActivityRow({ log, onOpen }: { log: ActivityLogEntry; onOpen?: () => void }) {
  const unified = hasFeature('unified_work_types');
  const laborOn = hasFeature('labor_categories');
  const bucket = laborOn ? bucketOfLog(log) : 'work';
  const accent = bucket === 'exempt' ? '#dc2626' : bucket === 'nonwork' ? '#64748b' : (unified ? '#1d4ed8' : (log.color || '#7c3aed'));
  return (
    <div onClick={onOpen}
      style={{ background: unified ? '#f8fafc' : '#faf5ff', borderRadius: 10, padding: '10px 12px', border: `1px solid ${unified ? '#f1f5f9' : '#e9d5ff'}`, cursor: onOpen ? 'pointer' : 'default' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{fmtTime(log.started_at) || '—'}</span>
            <span style={{ fontSize: 12, color: '#d8b4fe' }}>→</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{fmtTime(log.ended_at) || '—'}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {!hasFeature('unified_work_types') && (
              <span style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', background: '#f5f3ff', padding: '2px 8px', borderRadius: 99, border: '1px solid #ddd6fe' }}>⚡ 臨時業務</span>
            )}
            {laborOn && bucket !== 'work' && (
              <span style={{ fontSize: 11, fontWeight: 700, color: accent, background: accent + '14', padding: '2px 8px', borderRadius: 99, border: `1px solid ${accent}55` }}>
                {bucket === 'exempt' ? '別枠' : '労働外'}{log.labor_category_name ? `・${log.labor_category_name}` : ''}
              </span>
            )}
            <span style={{ fontSize: 12, fontWeight: 700, color: accent }}>{log.emoji} {log.name}</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: accent, background: accent + '18', padding: '2px 8px', borderRadius: 99 }}>{log.duration_minutes}分</span>
            {laborOn && log.reason && <span style={{ fontSize: 11, color: '#64748b' }}>事由: {log.reason}</span>}
          </div>
        </div>
        {onOpen && <span style={{ color: '#c4b5fd', fontSize: 18, flexShrink: 0 }}>›</span>}
      </div>
    </div>
  );
}

// ─── アルコール測定の業務フロー（alcohol_linked_attendance / alcohol_terminal_only）の案内 ─────────────
interface AlcoholGuide {
  /** 選んだ業務がアルコール必須か */
  required: boolean;
  /** 共有端末のみ登録: 個人アプリからは登録できない */
  blocked: boolean;
  /** 測定連動の勤怠: すでに始まった時刻（事後登録）→ 陸上の承認を経て反映 */
  approval: boolean;
  text: string;
}
function useAlcoholGuide(types: WorkTypeMaster[], workType?: string, startIso?: string): AlcoholGuide {
  // 通知のタイミング・有効範囲・通知先は陸上がマスタで決める（既定: 30分前・前後30分・陸上と船長）
  const st = useAlcoholWorkflowSettings();
  return useMemo(() => {
    const required = !!types.find(t => t.name === (workType ?? ''))?.requires_alcohol;
    if (!required) return { required: false, blocked: false, approval: false, text: '' };
    if (hasFeature('alcohol_terminal_only')) {
      return { required, blocked: true, approval: false, text: '指定されている勤務は共有端末での当日の勤怠登録が必須です。アルコール測定が必要なため、個人アプリからの事前入力・事後登録はできません。' };
    }
    if (hasFeature('alcohol_linked_attendance')) {
      const past = !startIso || new Date(startIso).getTime() <= Date.now();
      const remind = st.reminder_minutes > 0 ? `（${st.reminder_minutes}分前に通知します）` : '';
      const who = notifiedPartiesLabel(st);
      return past
        ? { required, blocked: false, approval: true, text: 'アルコール測定が必要な勤務の事後登録・修正は、陸上管理者の承認後に勤怠へ反映されます。' }
        : { required, blocked: false, approval: false, text: `この勤務はアルコール測定が必要です。出勤予定の${st.window_label}以内に、船内の共有端末の「アルコール測定のみ」で測定してください${remind}。${who ? `測定がないまま終了すると${who}に通知されます。` : ''}` };
    }
    return { required, blocked: false, approval: false, text: '' };
  }, [types, workType, startIso, st]);
}
function AlcoholGuideBanner({ guide }: { guide: AlcoholGuide }) {
  if (!guide.text) return null;
  const tone = guide.blocked ? { bg: '#fef2f2', bd: '#fecaca', fg: '#991b1b', icon: '⛔' }
    : guide.approval ? { bg: '#fff7ed', bd: '#fed7aa', fg: '#9a3412', icon: '🔏' }
    : { bg: '#fffbeb', bd: '#fde68a', fg: '#92400e', icon: '🧪' };
  return (
    <div role="status" style={{ background: tone.bg, border: `1px solid ${tone.bd}`, borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 12.5, color: tone.fg, lineHeight: 1.6, display: 'flex', gap: 8 }}>
      <span style={{ fontSize: 16, flexShrink: 0 }}>{tone.icon}</span><span>{guide.text}</span>
    </div>
  );
}
/** 共有端末で測った単独測定のうち、その時間帯のもの（勤怠の追加・修正で「この測定を使う」と選べる） */
interface CheckCandidate { id: string; measured_at: string; alcohol_value: number | null; is_manual: number; in_window: boolean; work_session_id?: string | null; vessel_name?: string | null }
function useCheckCandidates(date: string, clockIn: string, sessionId?: string): CheckCandidate[] {
  const [rows, setRows] = useState<CheckCandidate[]>([]);
  useEffect(() => {
    if (!hasFeature('alcohol_linked_attendance') || !date || !clockIn) { setRows([]); return; }
    let alive = true;
    const q = `/alcohol-workflow/checks/candidates?date=${date}&clock_in=${encodeURIComponent(clockIn)}${sessionId && !isLocalId(sessionId) ? `&session_id=${encodeURIComponent(sessionId)}` : ''}`;
    apiFetch<{ data: CheckCandidate[] }>(q).then(r => { if (alive) setRows(r?.data ?? []); }).catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [date, clockIn, sessionId]);
  return rows;
}
const fmtHm = (iso: string) => new Date(iso).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
function CheckPicker({ candidates, value, onChange }: { candidates: CheckCandidate[]; value: string; onChange: (id: string) => void }) {
  if (!candidates.length) return null;
  return (
    <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: '#0c4a6e', marginBottom: 2 }}>🧪 この時間帯のアルコール測定記録（共有端末）</div>
      <div style={{ fontSize: 11, color: '#0369a1', marginBottom: 8 }}>この勤怠の測定として使えます。該当するものを選んでください。</div>
      {candidates.map(c => {
        const selected = value === c.id;
        return (
          <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 8, background: selected ? '#fff' : 'transparent', border: `1px solid ${selected ? '#38bdf8' : 'transparent'}`, marginBottom: 4, cursor: 'pointer' }}>
            <input type="radio" name="alco-check" checked={selected} onChange={() => onChange(c.id)} />
            <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{fmtHm(c.measured_at)}</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>{c.alcohol_value != null ? `${Number(c.alcohol_value).toFixed(3)} mg/L` : '--'}</span>
            {c.is_manual === 1 && <span style={{ fontSize: 10, color: '#b45309', background: '#fef3c7', borderRadius: 6, padding: '1px 6px' }}>手入力</span>}
            {c.in_window
              ? <span style={{ fontSize: 10, color: '#166534', background: '#dcfce7', borderRadius: 6, padding: '1px 6px' }}>有効範囲内</span>
              : <span style={{ fontSize: 10, color: '#64748b', background: '#f1f5f9', borderRadius: 6, padding: '1px 6px' }}>時間が離れています</span>}
            {c.work_session_id && <span style={{ fontSize: 10, color: '#1d4ed8' }}>紐付け済み</span>}
          </label>
        );
      })}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', fontSize: 12, color: '#64748b', cursor: 'pointer' }}>
        <input type="radio" name="alco-check" checked={value === ''} onChange={() => onChange('')} />使わない
      </label>
    </div>
  );
}
/** 承認待ちになったときの結果シート */
function PendingResultSheet({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxWidth: 480, boxShadow: '0 -8px 32px rgba(0,0,0,0.15)' }}>
        <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🔏</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>承認を依頼しました</div>
          <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 8, lineHeight: 1.6 }}>{message}<br />結果は「連絡」のシステム通知でお知らせします。</div>
        </div>
        <button onClick={onClose} style={{ width: '100%', padding: 14, border: 'none', borderRadius: 12, background: '#0057d9', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>閉じる</button>
      </div>
    </div>
  );
}
interface MyRequest { id: string; target_date: string; requested_clock_in: string | null; requested_clock_out: string | null; requested_work_type: string | null; status: string; created_at: string }
/** 自分の承認待ち申請（alcohol_linked_attendance の会社のみ） */
function usePendingRequests(deps: unknown[]): MyRequest[] {
  const [rows, setRows] = useState<MyRequest[]>([]);
  useEffect(() => {
    if (!hasFeature('alcohol_linked_attendance')) return;
    let alive = true;
    apiFetch<{ data: MyRequest[] }>('/alcohol-workflow/requests?status=pending').then(r => { if (alive) setRows(r?.data ?? []); }).catch(() => { /* 圏外 */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return rows;
}

// ─── 修正モーダル ─────────────────────────────────────────────────────

function addOneDayStr(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}
function CrewEditModal({ record, date, onSave, onClose, fallbackVesselId }: {
  record: SessionRecord; date: string; onSave: () => void; onClose: () => void;
  /** 記録にvessel_idが無いとき（個人スマホ等）に業務種別マスタを引く船 */
  fallbackVesselId?: string;
}) {
  const [clockIn, setClockIn]   = useState(record.clockIn ?? '');
  const [clockOut, setClockOut] = useState(record.clockOut ?? '');
  const [workType, setWorkType] = useState(record.workType ?? '');
  // 休憩時間は記録済みの値を初期値にする（画面の勤務時間＝保存される総労働時間になるよう明示入力する）
  const [breakMin, setBreakMin] = useState(String(record.breakMin ?? 0));
  const [reason, setReason]     = useState('');
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(false);
  const [clockOutNextDay, setClockOutNextDay] = useState(false);
  const [pendingMsg, setPendingMsg] = useState('');
  const online = useOnlineStatus();
  const WORK_TYPES = useVesselWorkTypes(record.vessel_id || fallbackVesselId);
  const WORK_TYPE_OPTIONS = useMemo(() => WORK_TYPES.map(t => t.name), [WORK_TYPES]);
  const alcoGuide = useAlcoholGuide(WORK_TYPES, workType || record.workType);
  // その時間帯の単独測定（共有端末）を「この勤怠の測定」として選べる。既に紐付いているものは最初から選択
  const checkCandidates = useCheckCandidates(date, clockIn, record.id);
  const [checkId, setCheckId] = useState('');
  useEffect(() => { const linked = checkCandidates.find(c => c.work_session_id === record.id); if (linked) setCheckId(linked.id); }, [checkCandidates, record.id]);

  const nextDate = addOneDayStr(date);
  const breakNum = Math.max(0, Number(breakMin) || 0);

  const calcWork = () => {
    if (!clockIn || !clockOut) return null;
    const [ih = 0, im = 0] = clockIn.split(':').map(Number);
    const [oh = 0, om = 0] = clockOut.split(':').map(Number);
    const endMin = oh * 60 + om + (clockOutNextDay ? 1440 : 0);
    return Math.max(0, endMin - (ih * 60 + im) - breakNum);
  };

  const handleSave = async () => {
    // 備考は任意（2026-08-20 顧客要望: 理由必須を廃止）
    if (!clockIn && !clockOut) { setError('出勤か退勤の時刻を入力してください'); return; }
    setSaving(true); setError('');
    const body = {
      clock_in: clockIn || undefined, clock_out: clockOut || undefined,
      clock_out_date: clockOutNextDay ? nextDate : undefined,
      break_minutes: breakNum,
      work_type: workType || undefined, reason,
      alcohol_check_id: checkId || undefined,
    };
    const view = { clockIn: clockIn || undefined, clockOut: clockOut || undefined, breakMin: breakNum, workType: workType || undefined };
    // まだ送っていない「追加」を直すときは、キューの中身を書き換えるだけでよい
    const queueIt = () => {
      if (isLocalId(record.id)) {
        const mine = getEdits().find(x => x.session_id === record.id && x.kind === 'create');
        if (mine) {
          removeEdit(mine.id);
          enqueueEdit({ kind: 'create', target_date: date, session_id: record.id, body: { ...(mine.body ?? {}), ...body }, view });
          return;
        }
      }
      enqueueEdit({ kind: 'update', target_date: date, session_id: record.id, body, view });
    };
    try {
      if (isLocalId(record.id)) { queueIt(); onSave(); return; }   // 未送信の追加はサーバーにまだ無い
      const res = await apiFetch<any>(`/attendance/${record.id}`, { method: 'PUT', body: JSON.stringify(body) });
      // アルコール必須の勤怠の事後修正は陸上の承認待ちになる（alcohol_workflow_a）
      if (res?.data?.pending) { setPendingMsg(String(res.data.message ?? '陸上管理者の承認後に反映されます。')); return; }
      onSave();
    } catch (e: any) {
      // 圏外なら端末に貯めて、電波が入ったら自動で送る（沖で直せないと困るため）
      if (isOfflineError(e)) { queueIt(); onSave(); return; }
      setError(e.message ?? '保存に失敗しました');
    }
    finally { setSaving(false); }
  };

  const workMin = calcWork();
  if (pendingMsg) return <PendingResultSheet message={pendingMsg} onClose={onSave} />;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
      {/* maxHeight+スクロール: 横画面タブレットでシートが画面より高くなると、上端の時間入力が画面外に切れて操作できなくなる */}
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxWidth: 480, boxShadow: '0 -8px 32px rgba(0,0,0,0.15)', maxHeight: '85dvh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>勤怠を修正</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
              {date}{online ? '' : '（圏外: 端末に保存し、電波が入ったら送信します）'}
            </div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, border: 'none', background: '#f1f5f9', borderRadius: '50%', cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>開始時刻</span>
            <input type="time" value={clockIn} onChange={e => setClockIn(e.target.value)}
              style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 15, outline: 'none', background: '#f8fafc' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>終了時刻</span>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <input type="time" value={clockOut} onChange={e => setClockOut(e.target.value)}
                style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 15, outline: 'none', background: '#f8fafc' }} />
              <button type="button" onClick={() => setClockOutNextDay(p => !p)}
                style={{ padding: '8px 7px', fontSize: 10, fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer', background: clockOutNextDay ? '#7c3aed' : '#f1f5f9', color: clockOutNextDay ? '#fff' : '#64748b', whiteSpace: 'nowrap' }}>
                +1日
              </button>
            </div>
            {clockOutNextDay && <span style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600 }}>翌日 {nextDate}</span>}
          </label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>作業種別</span>
            <select value={workType} onChange={e => setWorkType(e.target.value)}
              style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, outline: 'none', background: '#f8fafc', color: '#0f172a' }}>
              <option value="">（未設定）</option>
              {workType && !WORK_TYPE_OPTIONS.includes(workType) && <option value={workType}>{workType}</option>}
              {WORK_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>休憩（分）</span>
            <input type="number" inputMode="numeric" min={0} step={5} value={breakMin}
              onChange={e => setBreakMin(e.target.value)}
              style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 15, outline: 'none', background: '#f8fafc' }} />
          </label>
        </div>
        <AlcoholGuideBanner guide={alcoGuide} />
        <CheckPicker candidates={checkCandidates} value={checkId} onChange={setCheckId} />
        {workMin !== null && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#166534' }}>
            修正後: <strong>{fmtMin(workMin)}</strong>
            {breakNum > 0 && <span style={{ color: '#64748b', marginLeft: 6, fontSize: 12 }}>（休憩 {breakNum}分を除く）</span>}
            {workMin > 480 && <span style={{ color: '#dc2626', marginLeft: 8 }}>残業 {fmtMin(workMin - 480)}</span>}
          </div>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#0057d9' }}>備考（任意）</span>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="例：打刻を忘れたため（空欄でも保存できます）"
            style={{ border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 12px', fontSize: 13, resize: 'none', fontFamily: 'inherit', background: '#eff6ff', outline: 'none' }} />
        </label>
        {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 14, border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>キャンセル</button>
          <button onClick={handleSave} disabled={saving || alcoGuide.blocked} style={{ flex: 2, padding: 14, border: 'none', borderRadius: 12, background: saving || alcoGuide.blocked ? '#93c5fd' : '#0057d9', color: '#fff', fontWeight: 700, cursor: saving || alcoGuide.blocked ? 'not-allowed' : 'pointer', fontSize: 14 }}>
            {saving ? '送信中...' : alcoGuide.blocked ? '共有端末で登録してください' : alcoGuide.approval ? '承認を依頼する' : online ? '保存する' : '端末に保存する'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 追加モーダル ─────────────────────────────────────────────────────
function NewEntryModal({ date, onSave, onClose, onSavedWithActivity, autoActivity, vesselId }: {
  date: string; onSave: () => void; onClose: () => void;
  onSavedWithActivity?: (id: string, vesselId: string, date: string) => void;
  autoActivity?: boolean;
  /** true = 個人スマホ: 直接追加でなく「登録依頼」として申請する */
  /** 業務種別マスタを引く船（個人スマホでも親のuserVesselIdから渡す） */
  vesselId?: string;
}) {
  const [clockIn, setClockIn]   = useState('09:00');
  const [clockOut, setClockOut] = useState('18:00');
  const [workType, setWorkType] = useState('');
  const WORK_TYPES = useVesselWorkTypes(vesselId);
  const WORK_TYPE_OPTIONS = useMemo(() => WORK_TYPES.map(t => t.name), [WORK_TYPES]);
  // アルコール必須の業務: 事前入力なら測定の案内、事後登録なら承認制、パターンBなら登録不可
  const alcoGuide = useAlcoholGuide(WORK_TYPES, workType, `${date}T${clockIn || '00:00'}:00+09:00`);
  const [pendingMsg, setPendingMsg] = useState('');
  // その時間帯の単独測定（共有端末）を「この勤怠の測定」として選べる。有効範囲内のものは最初から選択
  const checkCandidates = useCheckCandidates(date, clockIn);
  const [checkId, setCheckId] = useState('');
  useEffect(() => { if (!checkId) { const w = checkCandidates.find(c => c.in_window); if (w) setCheckId(w.id); } }, [checkCandidates]); // eslint-disable-line react-hooks/exhaustive-deps
  // 休憩は既定0分。入力した分だけ差し引くので、画面の勤務時間＝保存される総労働時間になる。
  const [breakMin, setBreakMin] = useState('0');
  const [reason, setReason]     = useState('');
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(false);
  const [clockOutNextDay, setClockOutNextDay] = useState(false);
  const [savedResult, setSavedResult] = useState<{ id: string; vessel_id: string; work_date: string } | null>(null);
  const online = useOnlineStatus();

  const nextDate = addOneDayStr(date);
  const breakNum = Math.max(0, Number(breakMin) || 0);

  const calcWork = () => {
    if (!clockIn || !clockOut) return null;
    const [ih = 0, im = 0] = clockIn.split(':').map(Number);
    const [oh = 0, om = 0] = clockOut.split(':').map(Number);
    const endMin = oh * 60 + om + (clockOutNextDay ? 1440 : 0);
    return Math.max(0, endMin - (ih * 60 + im) - breakNum);
  };

  const handleSave = async () => {
    // 備考は任意（2026-08-20 顧客要望: 理由必須を廃止）
    if (!clockIn) { setError('開始時刻を入力してください'); return; }
    setSaving(true); setError('');
    const body = {
      work_date: date, clock_in: clockIn, clock_out: clockOut || undefined,
      clock_out_date: clockOutNextDay ? nextDate : undefined,
      break_minutes: breakNum,
      work_type: workType || undefined,
      reason,
      alcohol_check_id: checkId || undefined,
    };
    try {
      const res = await apiFetch<{ data: { id: string; vessel_id: string; work_date: string; pending?: boolean; message?: string } }>('/attendance', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      // アルコール必須の勤怠の事後登録は陸上の承認待ちになる（alcohol_workflow_a）
      if (res?.data?.pending) { setPendingMsg(String(res.data.message ?? '陸上管理者の承認後に反映されます。')); return; }
      const sid = String(res?.data?.id ?? '');
      const vid = res?.data?.vessel_id ?? '';
      const wd  = res?.data?.work_date ?? date;
      if (autoActivity && onSavedWithActivity) {
        onSave();
        onSavedWithActivity(sid, vid, wd);
      } else if (onSavedWithActivity) {
        setSavedResult({ id: sid, vessel_id: vid, work_date: wd });
      } else {
        onSave();
      }
    } catch (e: any) {
      // 圏外なら端末に貯める。作業の追加はサーバーのIDが要るので、送信後に行ってもらう
      if (isOfflineError(e)) {
        enqueueEdit({
          kind: 'create', target_date: date, session_id: newLocalId(), body,
          view: { clockIn, clockOut: clockOut || undefined, breakMin: breakNum, workType: workType || undefined },
        });
        onSave();
        return;
      }
      setError(e.message ?? '保存に失敗しました');
    }
    finally { setSaving(false); }
  };

  if (pendingMsg) return <PendingResultSheet message={pendingMsg} onClose={onSave} />;
  if (savedResult) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
        <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxWidth: 480, boxShadow: '0 -8px 32px rgba(0,0,0,0.15)' }}>
          <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>勤怠を追加しました</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onSave} style={{ flex: 1, padding: 14, border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>閉じる</button>
            <button onClick={() => { onSave(); onSavedWithActivity!(savedResult.id, savedResult.vessel_id, savedResult.work_date); }}
              style={{ flex: 2, padding: 14, border: 'none', borderRadius: 12, background: '#7c3aed', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
              {hasFeature('unified_work_types') ? '作業を追加する' : '臨時作業を追加する'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const workMin = calcWork();
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
      {/* maxHeight+スクロール: 横画面タブレットでシートが画面より高くなると、上端の時間入力が画面外に切れて操作できなくなる */}
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxWidth: 480, boxShadow: '0 -8px 32px rgba(0,0,0,0.15)', maxHeight: '85dvh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>勤怠を追加</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
              {date}{online ? '' : '（圏外: 端末に保存し、電波が入ったら送信します）'}
            </div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, border: 'none', background: '#f1f5f9', borderRadius: '50%', cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>開始時刻</span>
            <input type="time" value={clockIn} onChange={e => setClockIn(e.target.value)}
              style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 15, outline: 'none', background: '#f8fafc' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>終了時刻</span>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              <input type="time" value={clockOut} onChange={e => setClockOut(e.target.value)}
                style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 15, outline: 'none', background: '#f8fafc' }} />
              <button type="button" onClick={() => setClockOutNextDay(p => !p)}
                style={{ padding: '8px 7px', fontSize: 10, fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer', background: clockOutNextDay ? '#7c3aed' : '#f1f5f9', color: clockOutNextDay ? '#fff' : '#64748b', whiteSpace: 'nowrap' }}>
                +1日
              </button>
            </div>
            {clockOutNextDay && <span style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600 }}>翌日 {nextDate}</span>}
          </label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>作業種別</span>
            <select value={workType} onChange={e => setWorkType(e.target.value)}
              style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, outline: 'none', background: '#f8fafc', color: '#0f172a' }}>
              <option value="">（未設定）</option>
              {WORK_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>休憩（分）</span>
            <input type="number" inputMode="numeric" min={0} step={5} value={breakMin}
              onChange={e => setBreakMin(e.target.value)}
              style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 15, outline: 'none', background: '#f8fafc' }} />
          </label>
        </div>
        <AlcoholGuideBanner guide={alcoGuide} />
        <CheckPicker candidates={checkCandidates} value={checkId} onChange={setCheckId} />
        {workMin !== null && workMin > 0 && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#166534' }}>
            勤務時間: <strong>{fmtMin(workMin)}</strong>
            {breakNum > 0 && <span style={{ color: '#64748b', marginLeft: 6, fontSize: 12 }}>（休憩 {breakNum}分を除く）</span>}
            {workMin > 480 && <span style={{ color: '#dc2626', marginLeft: 8 }}>残業 {fmtMin(workMin - 480)}</span>}
          </div>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#059669' }}>備考（任意）</span>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} placeholder="例：打刻漏れのため（空欄でも保存できます）"
            style={{ border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 12px', fontSize: 13, resize: 'none', fontFamily: 'inherit', background: '#f0fdf4', outline: 'none' }} />
        </label>
        {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 14, border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>キャンセル</button>
          <button onClick={handleSave} disabled={saving || alcoGuide.blocked} style={{ flex: 2, padding: 14, border: 'none', borderRadius: 12, background: saving || alcoGuide.blocked ? '#6ee7b7' : '#059669', color: '#fff', fontWeight: 700, cursor: saving || alcoGuide.blocked ? 'not-allowed' : 'pointer', fontSize: 14 }}>
            {saving ? '送信中...' : alcoGuide.blocked ? '共有端末で登録してください' : alcoGuide.approval ? '承認を依頼する' : online ? '追加する' : '端末に保存する'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── 特別作業モーダル ──────────────────────────────────────────────────
interface ActivityMaster {
  id: string; name: string; emoji: string; color: string;
  labor_category?: string | null; labor_category_name?: string | null;
  counts_as_work?: number; counts_toward_limit?: number; permits_over_limit?: number; requires_reason?: number; notify_shore?: number;
}
interface ActivityLog { id: string; activity_master_id: string; name: string; emoji: string; color: string; started_at: string; ended_at: string; duration_minutes: number; note?: string; reason?: string | null; labor_category_name?: string | null; counts_as_work?: number; counts_toward_limit?: number; permits_over_limit?: number; }
const BUCKET_UI: Record<'work' | 'exempt' | 'nonwork', { title: string; color: string; note: string }> = {
  work:    { title: '作業',            color: '#0057d9', note: '労働時間として数えます' },
  exempt:  { title: '緊急・安全対応',  color: '#dc2626', note: '労働時間の上限とは別枠で記録します（事由が必要）' },
  nonwork: { title: '労働時間外の記録', color: '#64748b', note: '労働時間には含めません' },
};
const REASON_PRESETS = ['荒天による避泊・操船', '人命救助（他船・海中転落）', '機関故障の応急対応', '火災・浸水などの非常事態', '入出港時の安全確保', 'その他（下に記入）'];

function ActivityLogModal({ sessionId, vesselId, sessionDate, onClose }: {
  sessionId: string;
  vesselId: string;
  sessionDate: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [activityTypes, setActivityTypes] = React.useState<ActivityMaster[]>([]);
  const [existingLogs, setExistingLogs] = React.useState<ActivityLog[]>([]);
  const [selected, setSelected] = React.useState<string>('');
  const [startTime, setStartTime] = React.useState('');
  const [endTime, setEndTime] = React.useState('');
  const [note, setNote] = React.useState('');
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [deleteConfirm, setDeleteConfirm] = React.useState<string | null>(null);
  // 労働区分（機能フラグ labor_categories）: 事由が必要な区分の入力
  const laborOn = hasFeature('labor_categories');
  const [reasonPreset, setReasonPreset] = React.useState('');
  const [reasonText, setReasonText] = React.useState('');
  const selectedType = activityTypes.find(t => t.id === selected);
  const needsReason = laborOn && selectedType?.requires_reason === 1;
  const reasonValue = reasonPreset && reasonPreset !== 'その他（下に記入）' ? (reasonText ? `${reasonPreset}：${reasonText}` : reasonPreset) : reasonText;
  const bucketOfType = (t?: ActivityMaster): 'work' | 'exempt' | 'nonwork' => !laborOn || !t ? 'work' : t.counts_as_work === 0 ? 'nonwork' : (t.counts_toward_limit === 0 || t.permits_over_limit === 1) ? 'exempt' : 'work';
  const typeGroups = (['work', 'exempt', 'nonwork'] as const).map(b => ({ bucket: b, items: activityTypes.filter(t => bucketOfType(t) === b) })).filter(g => g.items.length > 0);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [typesRes, logsRes] = await Promise.all([
          apiFetch<any>(`/vessels/${vesselId}/activity-types`),
          apiFetch<any>(`/activity-logs?work_session_id=${sessionId}`),
        ]);
        setActivityTypes(typesRes?.data ?? []);
        setExistingLogs(logsRes?.data ?? []);
      } catch { /* noop */ }
      finally { setLoading(false); }
    };
    load();
  }, [sessionId, vesselId]);

  const handleSave = async () => {
    if (!selected) { setError('作業種別を選択してください'); return; }
    if (!startTime || !endTime) { setError('開始・終了時刻を入力してください'); return; }
    const startIso = `${sessionDate}T${startTime}:00+09:00`;
    const endIso   = `${sessionDate}T${endTime}:00+09:00`;
    if (new Date(endIso) <= new Date(startIso)) { setError('終了時刻は開始時刻より後にしてください'); return; }
    if (needsReason && !reasonValue.trim()) { setError(`「${selectedType?.name ?? 'この作業'}」は事由の入力が必要です`); return; }
    setSaving(true); setError('');
    try {
      await apiFetch<any>('/activity-logs', {
        method: 'POST',
        body: JSON.stringify({
          work_session_id: sessionId, vessel_id: vesselId, activity_master_id: selected, started_at: startIso, ended_at: endIso, note: note || undefined,
          ...(laborOn && selectedType?.labor_category ? { labor_category: selectedType.labor_category } : {}),
          ...(needsReason ? { reason: reasonValue.trim() } : {}),
        }),
      });
      const res = await apiFetch<any>(`/activity-logs?work_session_id=${sessionId}`);
      setExistingLogs(res?.data ?? []);
      setSelected(''); setStartTime(''); setEndTime(''); setNote(''); setReasonPreset(''); setReasonText('');
    } catch (e: any) { setError(e.message ?? '保存に失敗しました'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiFetch<any>(`/activity-logs/${id}`, { method: 'DELETE' });
      setExistingLogs(p => p.filter(l => l.id !== id));
    } catch { /* noop */ }
    setDeleteConfirm(null);
  };

  const fmtIso = (iso: string) => {
    try { return new Date(iso).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo', hour12: false }); } catch { return iso; }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxWidth: 480, boxShadow: '0 -8px 32px rgba(0,0,0,0.15)', maxHeight: '85dvh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{hasFeature('unified_work_types') ? '作業を追加' : '臨時作業申請'}</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{sessionDate}</div>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, border: 'none', background: '#f1f5f9', borderRadius: '50%', cursor: 'pointer', fontSize: 16, color: '#64748b' }}>✕</button>
        </div>

        {/* 既存ログ */}
        {existingLogs.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>登録済み</div>
            {existingLogs.map(log => (
              <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f8fafc', borderRadius: 10, marginBottom: 6, border: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: 20 }}>{log.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{log.name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtIso(log.started_at)} → {fmtIso(log.ended_at)}（{log.duration_minutes}分）</div>
                </div>
                <button onClick={() => setDeleteConfirm(log.id)}
                  style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #fecaca', borderRadius: 6, background: '#fff', color: '#dc2626', cursor: 'pointer' }}>削除</button>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>読み込み中...</div>
        ) : activityTypes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
            {hasFeature('unified_work_types')
              ? 'この船舶に時間を指定して記録する作業が設定されていません。'
              : 'この船舶に特別作業が設定されていません。'}
            <br />管理者に設定を依頼してください。
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8 }}>作業を追加</div>
            {/* 作業種別（労働区分が有効なら 作業／緊急・安全対応／労働時間外 に分けて並べる） */}
            {(laborOn && typeGroups.length > 1 ? typeGroups : [{ bucket: 'work' as const, items: activityTypes }]).map(g => (
              <div key={g.bucket} style={{ marginBottom: 12 }}>
                {laborOn && typeGroups.length > 1 && (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: BUCKET_UI[g.bucket].color }}>{BUCKET_UI[g.bucket].title}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{BUCKET_UI[g.bucket].note}</span>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {g.items.map((t) => {
                    const accent = g.bucket === 'exempt' ? '#dc2626' : g.bucket === 'nonwork' ? '#64748b' : t.color;
                    return (
                      <button key={t.id} onClick={() => { setSelected(t.id); setError(''); }}
                        style={{ padding: '12px 8px', borderRadius: 12, border: selected === t.id ? `2px solid ${accent}` : '2px solid #e2e8f0', background: selected === t.id ? accent + '18' : '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer', position: 'relative' }}>
                        {selected === t.id && <div style={{ position: 'absolute', top: 6, right: 6, width: 16, height: 16, borderRadius: '50%', background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4"><polyline points="20 6 9 17 4 12"/></svg></div>}
                        <span style={{ fontSize: 22 }}>{t.emoji}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{t.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {laborOn && selectedType && bucketOfType(selectedType) !== 'work' && (
              <div style={{ background: bucketOfType(selectedType) === 'exempt' ? '#fef2f2' : '#f8fafc', border: `1px solid ${bucketOfType(selectedType) === 'exempt' ? '#fecaca' : '#e2e8f0'}`, borderRadius: 10, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
                <b style={{ color: bucketOfType(selectedType) === 'exempt' ? '#b91c1c' : '#334155' }}>{selectedType.labor_category_name ?? selectedType.name}</b>
                {selectedType.counts_as_work === 0 && '：労働時間には含めません。'}
                {selectedType.counts_toward_limit === 0 && '：労働時間に含めますが、上限計算からは除外されます。'}
                {selectedType.permits_over_limit === 1 && '：労働時間に含め、上限を超えても合法（割増）として扱います。'}
                {selectedType.notify_shore === 1 && ' 記録すると陸上へ通知されます。'}
              </div>
            )}
            {needsReason && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#b91c1c', marginBottom: 6 }}>事由（必須）</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {REASON_PRESETS.map(p => (
                    <button key={p} onClick={() => setReasonPreset(p)}
                      style={{ padding: '6px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: reasonPreset === p ? '2px solid #dc2626' : '1px solid #e2e8f0', background: reasonPreset === p ? '#fef2f2' : '#fff', color: reasonPreset === p ? '#b91c1c' : '#334155' }}>
                      {p}
                    </button>
                  ))}
                </div>
                <input type="text" value={reasonText} onChange={e => setReasonText(e.target.value)} placeholder="状況を一言（例：北東の風18m/s、○○港沖で錨泊待機）"
                  style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, outline: 'none', background: '#f8fafc' }} />
              </div>
            )}
            {/* 時刻 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>開始時刻</span>
                <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                  style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 15, outline: 'none', background: '#f8fafc' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>終了時刻</span>
                <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
                  style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 15, outline: 'none', background: '#f8fafc' }} />
              </label>
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>メモ（任意）</span>
              <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="例：荷役中の油水分離機確認"
                style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, outline: 'none', background: '#f8fafc' }} />
            </label>
            {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 10 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={onClose} style={{ flex: 1, padding: 14, border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>閉じる</button>
              <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: 14, border: 'none', borderRadius: 12, background: saving ? '#93c5fd' : '#0057d9', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>
                {saving ? '登録中...' : '登録する'}
              </button>
            </div>
          </>
        )}

        {deleteConfirm && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: 320 }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>
                {hasFeature('unified_work_types') ? 'この作業の記録を削除しますか？' : '特別作業を削除しますか？'}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: 12, border: '1px solid #e2e8f0', borderRadius: 10, cursor: 'pointer' }}>キャンセル</button>
                <button onClick={() => handleDelete(deleteConfirm)} style={{ flex: 1, padding: 12, border: 'none', borderRadius: 10, background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>削除</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 日付詳細シート ────────────────────────────────────────────────────
function DayDetailSheet({ record, userVesselId, onClose, onEdit, onDelete, onNewEntry, onActivity, onActivityForDate, onAddWork, actsBySession, workTypes }: {
  record: DayRecord;
  userVesselId: string;
  onClose: () => void;
  onEdit: (session: SessionRecord, date: string) => void;
  onDelete: (id: string, date: string) => void;
  onNewEntry: (date: string) => void;
  onActivity: (sessionId: string, vesselId: string, date: string) => void;
  onActivityForDate?: (date: string) => void;
  /** 日付(YYYY-MM-DD) → その日の臨時業務 */
  /** 統合版「業務を追加」を開く */
  onAddWork?: (date: string) => void;
  actsBySession?: Record<string, ActivityLogEntry[]>;
  /** 業務種別マスタ（作業名の絵文字表示に使う） */
  workTypes?: WorkTypeMaster[];
}) {
  const isSun = record.weekday === '日', isSat = record.weekday === '土';
  const dateColor = isSun ? '#ef4444' : isSat ? '#3b82f6' : '#0f172a';
  const month = record.date.slice(5, 7), day = record.date.slice(8, 10);
  // 臨時業務専用の入れ物セッションは勤怠として表示しない
  const realSessions = record.sessions.filter(s => !s.isActivityOnly);
  const dayActs = actsBySession?.[record.date] ?? [];
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, padding: '20px 16px 32px', maxHeight: '85vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: dateColor }}>
            {month}月{day}日（{record.weekday}）
          </span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8', lineHeight: 1 }}>✕</button>
        </div>
        {realSessions.length === 0 && dayActs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px 0 20px', color: '#94a3b8', fontSize: 13 }}>この日の勤怠記録はありません</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {realSessions.map(s => {
              const isActive = s.status === 'working';
              return (
                <div key={s.id} style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 12px', border: `1px solid ${isActive ? '#fde68a' : '#f1f5f9'}` }}>
                  <div style={{ marginBottom: 6 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{s.clockIn || '—'}</span>
                    <span style={{ color: '#cbd5e1', margin: '0 6px', fontSize: 12 }}>→</span>
                    {isActive
                      ? <span style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b', background: '#fffbeb', padding: '2px 8px', borderRadius: 99, border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>勤務中</span>
                      : <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{s.clockOut || '—'}</span>
                    }
                    <span style={{ fontSize: 11, color: '#0057d9', background: '#eff6ff', padding: '2px 7px', borderRadius: 99, marginLeft: 8 }}>{fmtMin(s.workMin ?? 0)}</span>
                    {(s.overMin ?? 0) > 0 && <span style={{ fontSize: 11, color: '#dc2626', background: '#fef2f2', padding: '2px 7px', borderRadius: 99, marginLeft: 4 }}>残業 {fmtMin(s.overMin ?? 0)}</span>}
                    {/* 何の作業だったか（臨時業務と同じ見せ方で表示） */}
                    <WorkTypeBadge workType={s.workType} types={workTypes ?? []} />
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => onActivity(s.id, s.vessel_id || userVesselId, record.date)}
                      style={{ fontSize: 11, padding: '5px 10px', border: '1px solid #c4b5fd', borderRadius: 8, background: '#f5f3ff', color: '#7c3aed', cursor: 'pointer', fontWeight: 600 }}>
                      {hasFeature('unified_work_types') ? '作業を追加' : '臨時作業'}
                    </button>
                    <button onClick={() => onEdit(s, record.date)}
                      style={{ fontSize: 11, padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: 600 }}>
                      修正
                    </button>
                    <button onClick={() => onDelete(s.id, record.date)}
                      style={{ fontSize: 11, padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}>
                      削除
                    </button>
                  </div>
                </div>
              );
            })}
            {/* 臨時業務も通常業務と同じ並びに表示する（日付単位） */}
            {dayActs.map(log => (
              <ActivityRow key={log.id} log={log}
                onOpen={() => onActivity(log.work_session_id, log.vessel_id || userVesselId, record.date)} />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
          {hasFeature('unified_work_types') ? (
            <button onClick={() => { onAddWork?.(record.date); onClose(); }}
              style={{ width: '100%', padding: 16, border: 'none', borderRadius: 12, background: '#0057d9', color: '#fff', cursor: 'pointer', fontSize: 16, fontWeight: 900 }}>
              ＋ 業務を追加
            </button>
          ) : (<>
          <button onClick={() => onNewEntry(record.date)}
            style={{ width: '100%', padding: 14, border: '1.5px dashed #cbd5e1', borderRadius: 12, background: '#f8fafc', color: '#64748b', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            ＋ 勤怠を追加
          </button>
          {onActivityForDate && (
            <button onClick={() => { onActivityForDate(record.date); onClose(); }}
              style={{ width: '100%', padding: 14, border: '1.5px dashed #c4b5fd', borderRadius: 12, background: '#faf5ff', color: '#7c3aed', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              ＋ 臨時作業を追加
            </button>
          )}
          </>)}
        </div>
      </div>
    </div>
  );
}

// ─── メインページ ─────────────────────────────────────────────────────
export const HistoryPage: React.FC<Props> = ({ user: _user }) => {
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [records, setRecords]       = useState<DayRecord[]>([]);
  const [loading, setLoading]       = useState(true);
  const [isOfflineCache, setIsOfflineCache] = useState(false);
  const [editTarget, setEditTarget] = useState<{ session: SessionRecord; date: string } | null>(null);
  const [newEntryDate, setNewEntryDate]   = useState<string | null>(null);
  /** 統合版「業務を追加」の対象日（unified_work_types 有効時はこちらだけを使う） */
  const [addWorkDate, setAddWorkDate]     = useState<string | null>(null);
  const [newEntryAutoActivity, setNewEntryAutoActivity] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; date: string } | null>(null);
  const [activityModal, setActivityModal] = React.useState<{ sessionId: string; vesselId: string; date: string } | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [actsBySession, setActsBySession] = useState<Record<string, ActivityLogEntry[]>>({});
  // 2026-08-20 顧客要望: 船員も管理端末と同じく直接修正・即時反映にする（承認制の「修正依頼」は廃止）。
  // 圏外のときはサーバーへ送れないので端末に貯め（utils/pendingEdits.ts）、電波が入ったら自動で送る。
  const [pendingEdits, setPendingEdits] = useState<PendingEdit[]>(() => getEdits());
  const refreshPending = () => setPendingEdits(getEdits());
  // アルコール必須の勤怠の事後登録・修正で、陸上の承認を待っている申請（alcohol_workflow_a）
  const [requestsKey, setRequestsKey] = useState(0);
  const myRequests = usePendingRequests([requestsKey, year, month]);

  /** 貯めた修正を送る。送れたら一覧を取り直す */
  const flushNow = async () => {
    if (!navigator.onLine || getEdits().length === 0) return;
    const r = await flushEdits((path, opts) => apiFetch<any>(path, opts));
    refreshPending();
    if (r.synced > 0) fetchDataRef.current?.();
  };
  const fetchDataRef = React.useRef<(() => void) | null>(null);
  useEffect(() => {
    flushNow();
    const onOnline = () => { flushNow(); };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const CACHE_KEY = `catms_history_${year}_${String(month).padStart(2, '0')}`;
  // 送信完了後に一覧を取り直すため、最新の fetchData を参照できるようにしておく

  /**
   * サーバー（または端末キャッシュ）の内容に、まだ送っていない修正を重ねて返す。
   * キャッシュ自体は書き換えないので、送信が済めばサーバーの内容にそのまま戻る。
   */
  const overlayPending = (days: DayRecord[]): DayRecord[] => {
    const ym = `${year}-${String(month).padStart(2, '0')}`;
    const edits = getEditsForMonth(ym);
    if (!edits.length) return days;
    const calc = (ci?: string, co?: string, br = 0) => {
      if (!ci || !co) return undefined;
      const [ih = 0, im = 0] = ci.split(':').map(Number);
      const [oh = 0, om = 0] = co.split(':').map(Number);
      let end = oh * 60 + om;
      if (end < ih * 60 + im) end += 1440;   // 日をまたぐ
      return Math.max(0, end - (ih * 60 + im) - br);
    };
    return days.map(d => {
      const mine = edits.filter(e => e.target_date === d.date);
      if (!mine.length) return d;
      let sessions = [...d.sessions];
      for (const e of mine) {
        if (e.kind === 'delete') {
          sessions = sessions.filter(x => x.id !== e.session_id);
        } else if (e.kind === 'update') {
          sessions = sessions.map(x => x.id !== e.session_id ? x : {
            ...x,
            clockIn: e.view?.clockIn ?? x.clockIn,
            clockOut: e.view?.clockOut ?? x.clockOut,
            breakMin: e.view?.breakMin ?? x.breakMin,
            workType: e.view?.workType ?? x.workType,
            workMin: calc(e.view?.clockIn ?? x.clockIn, e.view?.clockOut ?? x.clockOut, e.view?.breakMin ?? x.breakMin ?? 0) ?? x.workMin,
            pending: true,
          });
        } else {
          sessions.push({
            id: e.session_id,
            clockIn: e.view?.clockIn, clockOut: e.view?.clockOut,
            breakMin: e.view?.breakMin, workType: e.view?.workType,
            workMin: calc(e.view?.clockIn, e.view?.clockOut, e.view?.breakMin ?? 0),
            status: e.view?.clockOut ? 'completed' : 'working',
            recordSource: 'crew_edit',
            pending: true,
          });
        }
      }
      return { ...d, sessions };
    });
  };

  const fetchData = async () => {
    setLoading(true);
    setIsOfflineCache(false);
    try {
      const res = await apiFetch<any>('/timestamps/sessions?year=' + year + '&month=' + String(month).padStart(2, '0') + '&limit=100');
      const sessions: any[] = res?.data ?? res?.sessions ?? [];
      const byDate: Record<string, any[]> = {};
      for (const s of sessions) {
        const key = (s.target_date ?? s.started_at?.slice(0, 10)) as string;
        if (!key) continue;
        if (!byDate[key]) byDate[key] = [];
        byDate[key].push(s);
      }
      const days = getDaysInMonth(year, month).map(dateStr => {
        const dow = new Date(dateStr).getDay();
        const daySessions = byDate[dateStr] ?? [];
        const mapped: SessionRecord[] = daySessions.map((s: any) => {
          let recordSource: RecordSource = 'gps_auto';
          try {
            const h = JSON.parse(s.note ?? '[]');
            if (Array.isArray(h) && h.length > 0) {
              recordSource = h[h.length - 1].role === 'crew' ? 'crew_edit' : 'admin_input';
            }
          } catch { /* noop */ }
          const status: SessionRecord['status'] =
            s.session_status === 'completed' ? 'completed' :
            (s.session_status === 'working' || s.session_status === 'resting') ? 'working' : 'completed';
          return {
            id: s.id as string,
            vessel_id: s.vessel_id as string | undefined,
            isActivityOnly: !!s.is_activity_only,
            adminOperated: Number(s.admin_operated ?? 0) === 1,
            clockIn: fmtTime(s.started_at), clockOut: s.ended_at ? fmtTime(s.ended_at) : undefined,
            workMin: s.total_work_minutes ?? 0, overMin: s.total_overtime_minutes ?? 0, breakMin: s.total_break_minutes ?? 0,
            status, recordSource, alcoholValue: s.alcohol_value != null ? Number(s.alcohol_value) : undefined,
            workType: s.work_type ?? undefined,
          };
        });
        return { date: dateStr, weekday: WEEKDAYS[dow] ?? '日', sessions: mapped };
      });
      setRecords(overlayPending(days));

      // 臨時業務ログを取得して「日付別」にまとめる。
      // セッション別にすると、所属セッションを消した/差し替えたときに表示から消えてしまうため日付を軸にする。
      let bySession: Record<string, ActivityLogEntry[]> = {};
      try {
        const padM    = String(month).padStart(2, '0');
        const lastDay = new Date(year, month, 0).getDate();
        const actRes  = await apiFetch<any>(
          `/activity-logs?user_id=${encodeURIComponent(_user.user_id)}&date_from=${year}-${padM}-01&date_to=${year}-${padM}-${String(lastDay).padStart(2,'0')}`
        );
        for (const log of actRes?.data ?? []) {
          // 管理者の操作（船員デモ等）の中で付けた作業は勤怠ではないので出さない
          if (Number(log.session_admin_operated ?? 0) === 1) continue;
          const key = (log.target_date as string | undefined) ?? jstDateOf(log.started_at);
          if (!key) continue;
          if (!bySession[key]) bySession[key] = [];
          bySession[key].push(log as ActivityLogEntry);
        }
        for (const k of Object.keys(bySession)) {
          bySession[k]!.sort((a, b) => (a.started_at || '').localeCompare(b.started_at || ''));
        }
      } catch { /* ignore */ }
      setActsBySession(bySession);

      // オフライン用キャッシュに保存（未送信ぶんは重ねる前の内容を残す）
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ days, bySession, savedAt: new Date().toISOString() }));
      } catch { /* localStorage 容量超過は無視 */ }
      setRecords(overlayPending(days));
    } catch {
      // オンラインフェッチ失敗 → キャッシュを表示
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { days, bySession } = JSON.parse(cached) as { days: DayRecord[]; bySession: Record<string, ActivityLogEntry[]>; savedAt: string };
          setRecords(overlayPending(days));
          setActsBySession(bySession);
          setIsOfflineCache(true);
          return;
        }
      } catch { /* ignore */ }
      setRecords(getDaysInMonth(year, month).map(dateStr => ({
        date: dateStr, weekday: WEEKDAYS[new Date(dateStr).getDay()] ?? '日', sessions: [],
      })));
      setActsBySession({});
    } finally { setLoading(false); }
  };
  // 送信完了後に一覧を取り直せるよう、最新の fetchData を保持しておく
  fetchDataRef.current = fetchData;

  const handleDelete = async (id: string, date: string) => {
    // まだ送っていない「追加」ならキューから取り消すだけでよい
    if (isLocalId(id)) {
      removeEditsBySession(id);
      setDeleteConfirm(null); refreshPending(); fetchData();
      return;
    }
    try {
      await apiFetch<any>(`/attendance/${id}`, { method: 'DELETE' });
    } catch (e) {
      // 圏外なら端末に貯めて、電波が入ったら送る
      if (isOfflineError(e)) enqueueEdit({ kind: 'delete', target_date: date, session_id: id });
    }
    setDeleteConfirm(null);
    refreshPending();
    triggerSync();
    fetchData();
  };

  useEffect(() => { fetchData(); }, [year, month]);

  // 出勤日数・勤務時間は通常勤怠のみで集計する（臨時業務だけの入れ物は出勤に数えない）
  // 管理者の操作（船員デモ等）は勤怠ではないので、合計・内訳に入れない
  const allSessions = records.flatMap(r => r.sessions.filter(s => !s.isActivityOnly && !s.adminOperated));
  const worked      = records.filter(r => r.sessions.some(s => !s.isActivityOnly && !s.adminOperated));
  const userVesselId = useMemo(() => {
    for (const r of [...records].reverse()) {
      for (const s of r.sessions) { if (s.vessel_id) return s.vessel_id; }
    }
    return '';
  }, [records]);
  // 業務種別マスタ（作業名の絵文字表示に使う。宣言順に注意＝userVesselId確定後に呼ぶ）
  const vesselWorkTypes = useVesselWorkTypes(userVesselId);
  const totalWork   = allSessions.reduce((s, r) => s + (r.workMin ?? 0), 0);
  const totalOver   = allSessions.reduce((s, r) => s + (r.overMin ?? 0), 0);
  // 内訳に出すのは退勤済みの記録だけ。勤務中は勤務時間がまだ確定していないため、
  // 混ぜると「訓練 0h（1回）」のように0時間の作業として並んでしまう。
  const finishedSessions = allSessions.filter(s => s.status !== 'working');
  // 通常業務の作業種別ごとの合計（臨時業務の内訳表示と対になる集計）
  const workTypeTotals: Record<string, number> = {};
  for (const s of finishedSessions) {
    const key = s.workType || '未設定';
    workTypeTotals[key] = (workTypeTotals[key] ?? 0) + (s.workMin ?? 0);
  }
  // 統合表示用: 作業名ごとに「合計時間」と「回数」をまとめる。
  // 回数は 出退勤の記録1件＝1回 ／ 時間を指定した作業ログ1件＝1回 として数え、同じ作業名なら合算する。
  const unifiedTypeTotals: Record<string, { min: number; count: number; emoji: string }> = {};
  for (const s of finishedSessions) {
    const key = s.workType || '未設定';
    if (!unifiedTypeTotals[key]) unifiedTypeTotals[key] = { min: 0, count: 0, emoji: s.workType ? workTypeEmoji(vesselWorkTypes, s.workType) : '📋' };
    unifiedTypeTotals[key]!.min += s.workMin ?? 0;
    unifiedTypeTotals[key]!.count += 1;
  }
  const allActivityLogs = Object.values(actsBySession).flat();
  const totalActivityMin = allActivityLogs.reduce((s, l) => s + l.duration_minutes, 0);
  // 特別作業のうち、通常勤務の時間帯に含まれない分（＝勤務外に行った作業）の合計。
  // 勤務中に行った特別作業はその勤務時間に既に含まれているため、そのまま足すと二重計上になる。
  // 総勤務にはこの「はみ出した分」だけを加算する（合計＝通常勤務＋勤務外の特別作業）。
  const activityExtraMin = useMemo(() => {
    // ISO日時 → 基準日 00:00 からの経過分（JST・日またぎは 1440 以上になる）
    const jstMinFrom = (iso: string, baseDate: string) => {
      const jst = new Date(new Date(iso).getTime() + 9 * 3600 * 1000);
      const min = jst.getUTCHours() * 60 + jst.getUTCMinutes();
      const dayDiff = Math.round(
        (Date.parse(`${jst.toISOString().slice(0, 10)}T00:00:00Z`) - Date.parse(`${baseDate}T00:00:00Z`)) / 86_400_000
      );
      return min + dayDiff * 1440;
    };
    const byDate: Record<string, DayRecord> = {};
    for (const rec of records) byDate[rec.date] = rec;
    /** その日の勤務時間帯（基準日00:00からの分）。offsetは前日ぶんを -1440 するために使う */
    const busyOf = (rec: DayRecord | undefined, offset: number): [number, number][] => {
      const out: [number, number][] = [];
      for (const s of rec?.sessions ?? []) {
        if (s.isActivityOnly || !s.clockIn) continue;
        const [sh = 0, sm = 0] = s.clockIn.split(':').map(Number);
        const start = sh * 60 + sm;
        if (!s.clockOut) { out.push([start + offset, 4320]); continue; }
        const [eh = 0, em = 0] = s.clockOut.split(':').map(Number);
        let end = eh * 60 + em;
        while (end < start) end += 1440; // 日またぎ勤務
        out.push([start + offset, end + offset]);
      }
      return out;
    };
    let extra = 0;
    for (const rec of records) {
      const logs = actsBySession[rec.date] ?? [];
      if (logs.length === 0) continue;
      // 当日の勤務時間帯に加えて、前日から日をまたいで続く勤務も対象にする
      // （深夜作業が前日始まりのセッションに含まれている場合の二重計上を防ぐ）。
      const prevDate = new Date(Date.parse(`${rec.date}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
      const busy: [number, number][] = [...busyOf(rec, 0), ...busyOf(byDate[prevDate], -1440)];
      // 重なり合う勤務時間帯を1本にまとめる（同じ時間を二重に差し引かないため）
      busy.sort((a, b) => a[0] - b[0]);
      const merged: [number, number][] = [];
      for (const iv of busy) {
        const last = merged[merged.length - 1];
        if (last && iv[0] <= last[1]) last[1] = Math.max(last[1], iv[1]);
        else merged.push([iv[0], iv[1]]);
      }
      for (const log of logs) {
        const a = jstMinFrom(log.started_at, rec.date);
        let b = jstMinFrom(log.ended_at, rec.date);
        if (b < a) b += 1440;
        let free = b - a;
        for (const [bs, be] of merged) {
          const ov = Math.min(b, be) - Math.max(a, bs);
          if (ov > 0) free -= ov;
        }
        extra += Math.max(0, free);
      }
    }
    return extra;
  }, [records, actsBySession]);
  // 労働区分（機能フラグ labor_categories）: 労働／別枠／労働外の3本に分ける。
  //   労働外（食事・自発・私的…）は労働時間に含めない → 勤務中に行った分は総勤務から引き、勤務外の分は足さない。
  //   別枠は労働時間に含めたまま、上限計算から除外（68条）／上限超え許容（64条）として別に見せる。
  const laborOn = hasFeature('labor_categories');
  const laborBuckets = useMemo(() => {
    const r = { nonWork: 0, nonWorkInside: 0, nonWorkOutside: 0, exempt: 0, safetyOt: 0, byCode: {} as Record<string, { name: string; min: number }> };
    if (!laborOn) return r;
    const byDate: Record<string, DayRecord> = {};
    for (const rec of records) byDate[rec.date] = rec;
    // 勤怠は 'HH:MM' しか持たないので、activityExtraMin と同じく「基準日 0:00 からの分」で重なりを見る
    const jstMinFrom = (iso: string, baseDate: string) => {
      const jst = new Date(new Date(iso).getTime() + 9 * 3600 * 1000);
      const min = jst.getUTCHours() * 60 + jst.getUTCMinutes();
      const dayDiff = Math.round((Date.parse(`${jst.toISOString().slice(0, 10)}T00:00:00Z`) - Date.parse(`${baseDate}T00:00:00Z`)) / 86_400_000);
      return min + dayDiff * 1440;
    };
    const busyOf = (rec: DayRecord | undefined, offset: number): [number, number][] => {
      const out: [number, number][] = [];
      for (const s of rec?.sessions ?? []) {
        if (s.isActivityOnly || !s.clockIn) continue;
        const [sh = 0, sm = 0] = s.clockIn.split(':').map(Number);
        const start = sh * 60 + sm;
        if (!s.clockOut) { out.push([start + offset, 4320]); continue; }
        const [eh = 0, em = 0] = s.clockOut.split(':').map(Number);
        let end = eh * 60 + em;
        while (end < start) end += 1440;
        out.push([start + offset, end + offset]);
      }
      return out;
    };
    for (const rec of records) {
      const logs = actsBySession[rec.date] ?? [];
      if (!logs.length) continue;
      const prevDate = new Date(Date.parse(`${rec.date}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
      const spans = [...busyOf(rec, 0), ...busyOf(byDate[prevDate], -1440)];
      for (const l of logs) {
        const b = bucketOfLog(l);
        if (b === 'work') continue;
        const dur = l.duration_minutes ?? 0;
        if (b === 'nonwork') {
          const a = jstMinFrom(l.started_at, rec.date); let e = jstMinFrom(l.ended_at, rec.date); if (e < a) e += 1440;
          let inside = 0;
          for (const [s, t] of spans) { const ov = Math.min(e, t) - Math.max(a, s); if (ov > 0) inside += ov; }
          inside = Math.min(dur, Math.round(inside));
          r.nonWork += dur; r.nonWorkInside += inside; r.nonWorkOutside += dur - inside;
          const key = l.labor_category ?? 'nonwork';
          r.byCode[key] = { name: l.labor_category_name ?? l.name, min: (r.byCode[key]?.min ?? 0) + dur };
        } else {
          if (l.counts_toward_limit === 0) r.exempt += dur;
          if (l.permits_over_limit === 1) r.safetyOt += dur;
        }
      }
    }
    return r;
  }, [records, actsBySession, laborOn]);
  // 画面に出す総勤務（通常勤務＋勤務外の特別作業）。労働外は勤務中に行った分を引き、勤務外に行った分は足さない
  const totalWorkWithActivity = Math.max(0, totalWork + activityExtraMin - laborBuckets.nonWorkInside - laborBuckets.nonWorkOutside);
  const totalLimitMin = Math.max(0, totalWorkWithActivity - laborBuckets.exempt);
  const activityTypeTotals: Record<string, { min: number; emoji: string; color: string }> = {};
  for (const log of allActivityLogs) {
    if (!activityTypeTotals[log.name]) activityTypeTotals[log.name] = { min: 0, emoji: log.emoji, color: log.color };
    activityTypeTotals[log.name]!.min += log.duration_minutes;
    // 統合表示では時間を指定して記録した作業も同じ作業名の内訳として合算する（1件＝1回）
    if (!unifiedTypeTotals[log.name]) unifiedTypeTotals[log.name] = { min: 0, count: 0, emoji: log.emoji };
    unifiedTypeTotals[log.name]!.min += log.duration_minutes;
    unifiedTypeTotals[log.name]!.count += 1;
  }
  const todayStr    = now.toISOString().slice(0, 10);

  const prevMonth = () => { if (month === 1) { setYear(y => y - 1); setMonth(12); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setYear(y => y + 1); setMonth(1); } else setMonth(m => m + 1); };

  const handleActivityForDate = async (date: string) => {
    const rec = records.find(r => r.date === date);
    // その日に通常勤怠があればそこへ紐づける（無ければサーバー側で臨時業務専用の入れ物を作る）
    const real = rec?.sessions.find(s => !s.isActivityOnly) ?? rec?.sessions[0];
    if (real) {
      setActivityModal({ sessionId: real.id, vesselId: real.vessel_id || userVesselId, date });
      return;
    }
    try {
      const res = await apiFetch<{ data: { id: string; vessel_id: string } }>('/activity-logs/session', {
        method: 'POST',
        body: JSON.stringify({ target_date: date, vessel_id: userVesselId || undefined }),
      });
      const sid = res?.data?.id;
      if (!sid) throw new Error('no session');
      setActivityModal({ sessionId: sid, vesselId: res.data.vessel_id || userVesselId, date });
      fetchData();
    } catch {
      alert(hasFeature('unified_work_types')
        ? '作業の記録を作成できませんでした。通信環境をご確認ください。'
        : '臨時業務の記録を作成できませんでした。通信環境をご確認ください。');
    }
  };

  // カレンダー用 - 月の最初の曜日
  const firstDow = new Date(year, month - 1, 1).getDay();
  const calendarCells: (DayRecord | null)[] = [
    ...Array(firstDow).fill(null),
    ...records,
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, background: '#f8fafc', fontFamily: "'Inter', 'Hiragino Sans', sans-serif" }}>

      {/* ─── ヘッダー ─────────────────────────────── */}
      <div className="app-header" style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 16px 0', flexShrink: 0 }}>
        {/* タイトル行（戻るボタン＋画面名）。前の画面に戻れないと詰まるので必ず置く */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <BackButton />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.3px' }}>勤怠履歴</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 1 }}>{_user.user_name}</div>
          </div>
          {isOfflineCache && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6, padding: '3px 8px', flexShrink: 0 }}>
              オフライン
            </span>
          )}
        </div>

        {/* 月ナビ（押しやすい大きさにして横幅いっぱいに） */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f8fafc', borderRadius: 12, padding: 6, border: '1px solid #e2e8f0', marginBottom: 12 }}>
          <button onClick={prevMonth} aria-label="前の月"
            style={{ width: 44, height: 40, border: 'none', background: '#fff', borderRadius: 10, cursor: 'pointer', fontSize: 20, color: '#334155', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>‹</button>
          <span style={{ flex: 1, fontSize: 17, fontWeight: 800, color: '#0f172a', textAlign: 'center' }}>
            {year}年{month}月
          </span>
          <button onClick={nextMonth} aria-label="次の月"
            style={{ width: 44, height: 40, border: 'none', background: '#fff', borderRadius: 10, cursor: 'pointer', fontSize: 20, color: '#334155', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>›</button>
        </div>
      </div>

      {/* 統計・内訳・カレンダー・日別リストは、まとめて1つのスクロール領域に入れる。
          以前はカレンダーまでがヘッダー(flexShrink:0)の中にあり、業務の内訳が増えて
          ヘッダーが画面より高くなると、下のリストの高さが0になってスクロールできなくなっていた。 */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
        {myRequests.length > 0 && (
          <div style={{ margin: '10px 16px 0', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: '10px 12px' }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: '#9a3412', marginBottom: 4 }}>🔏 陸上の承認待ち {myRequests.length}件（アルコール測定が必要な勤務の事後登録）</div>
            {myRequests.map(r => (
              <div key={r.id} style={{ fontSize: 12, color: '#7c2d12', lineHeight: 1.7 }}>
                {r.target_date.slice(5).replace('-', '/')} {r.requested_clock_in ?? '--:--'}〜{r.requested_clock_out ?? '--:--'} {r.requested_work_type ?? ''}
              </div>
            ))}
          </div>
        )}
        <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 16px' }}>

        {/* 統計 3枚 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: totalActivityMin > 0 ? 6 : 14 }}>
          {[
            { label: '出勤日数', value: `${worked.length}日`, color: '#0057d9', bg: '#eff6ff' },
            { label: '総勤務',   value: fmtMin(totalWorkWithActivity), color: '#059669', bg: '#f0fdf4' },
            { label: '残業',     value: fmtMin(totalOver),    color: totalOver > 0 ? '#dc2626' : '#94a3b8', bg: totalOver > 0 ? '#fef2f2' : '#f8fafc', dim: !totalOver },
          ].map(item => (
            <div key={item.label} style={{ background: item.bg, borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: item.color, lineHeight: 1, letterSpacing: '-0.5px' }}>{item.value}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: item.color, opacity: 0.65, marginTop: 4 }}>{item.label}</div>
            </div>
          ))}
        </div>
        {/* 労働区分: 労働／別枠／労働外 の3本（区分の記録がある月だけ出す） */}
        {laborOn && (laborBuckets.nonWork > 0 || laborBuckets.exempt > 0 || laborBuckets.safetyOt > 0) && (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '10px 12px', marginBottom: 14, display: 'grid', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: '#0057d9' }}>労働時間</span>
              <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{fmtMin(totalWorkWithActivity)}
                {laborBuckets.exempt > 0 && <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginLeft: 6 }}>上限判定 {fmtMin(totalLimitMin)}</span>}
              </span>
            </div>
            {(laborBuckets.exempt > 0 || laborBuckets.safetyOt > 0) && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#dc2626' }}>別枠（緊急・安全）</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                  {laborBuckets.exempt > 0 && <span style={{ marginLeft: 8 }}>非常作業 {fmtMin(laborBuckets.exempt)}</span>}
                  {laborBuckets.safetyOt > 0 && <span style={{ marginLeft: 8 }}>安全臨時 {fmtMin(laborBuckets.safetyOt)}</span>}
                </span>
              </div>
            )}
            {laborBuckets.nonWork > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#64748b' }}>労働時間外の記録</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                  {fmtMin(laborBuckets.nonWork)}
                  <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginLeft: 6 }}>
                    {Object.values(laborBuckets.byCode).map(v => `${v.name} ${fmtMin(v.min)}`).join('・')}
                  </span>
                </span>
              </div>
            )}
          </div>
        )}
        {/* 統合表示: 何をどれだけ作業したかが分かるよう「作業名 / 合計時間 / 回数」で内訳を出す */}
        {hasFeature('unified_work_types') ? (
          Object.keys(unifiedTypeTotals).length > 0 && (
            <div style={{ background: '#f0f7ff', borderRadius: 12, padding: '8px 12px', marginBottom: 14, border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
              <div>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#0057d9', opacity: 0.7 }}>業務　</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#0057d9' }}>{fmtMin(totalWork + activityExtraMin)}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {Object.entries(unifiedTypeTotals).sort((a, b) => b[1].min - a[1].min).map(([name, v]) => (
                  <span key={name} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#dbeafe', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 600 }}>
                    {v.emoji} {name} {fmtMin(v.min)}（{v.count}回）
                  </span>
                ))}
              </div>
            </div>
          )
        ) : (<>
        {/* 通常業務の作業種別内訳（臨時業務の内訳と対になる表示） */}
        {Object.keys(workTypeTotals).length > 0 && (
          <div style={{ background: '#f0f7ff', borderRadius: 12, padding: '8px 12px', marginBottom: 8, border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
            <div>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#0057d9', opacity: 0.7 }}>通常業務　</span>
              <span style={{ fontSize: 18, fontWeight: 800, color: '#0057d9' }}>{fmtMin(totalWork)}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {Object.entries(workTypeTotals).sort((a, b) => b[1] - a[1]).map(([name, min]) => (
                <span key={name} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#dbeafe', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 600 }}>
                  {name === '未設定' ? '📋' : workTypeEmoji(vesselWorkTypes, name)} {name} {fmtMin(min)}
                </span>
              ))}
            </div>
          </div>
        )}
        {/* 特別作業合計 */}
        {totalActivityMin > 0 && (
          <div style={{ background: '#faf5ff', borderRadius: 12, padding: '8px 12px', marginBottom: 14, border: '1px solid #ddd6fe', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
            <div>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', opacity: 0.7 }}>特別作業　</span>
              <span style={{ fontSize: 18, fontWeight: 800, color: '#7c3aed' }}>{fmtMin(totalActivityMin)}</span>
              {/* 総勤務にどれだけ上乗せされたかを明示（勤務中に行った分は通常勤務に含まれる） */}
              <span style={{ fontSize: 10, color: '#7c3aed', opacity: 0.8, marginLeft: 6 }}>
                {activityExtraMin > 0
                  ? `うち勤務外 ${fmtMin(activityExtraMin)} を総勤務に加算`
                  : '通常勤務の時間内に含まれています'}
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {Object.entries(activityTypeTotals).map(([name, { min, emoji, color }]) => (
                <span key={name} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: color + '22', color, border: `1px solid ${color}44`, fontWeight: 600 }}>
                  {emoji} {name} {fmtMin(min)}
                </span>
              ))}
            </div>
          </div>
        )}
        </>)}

        {/* カレンダーグリッド */}
        {!loading && (
          <div style={{ marginBottom: 12 }}>
            {/* 曜日ヘッダー */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: 4 }}>
              {WEEKDAYS.map((d, i) => (
                <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: i === 0 ? '#ef4444' : i === 6 ? '#3b82f6' : '#94a3b8', paddingBottom: 4 }}>{d}</div>
              ))}
            </div>
            {/* 日付グリッド */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3 }}>
              {calendarCells.map((r, i) => {
                if (!r) return <div key={`e-${i}`} />;
                const dayNum = parseInt(r.date.slice(8));
                const hasWork = r.sessions.some(s => !s.isActivityOnly);
                const isWorking = r.sessions.some(s => !s.isActivityOnly && s.status === 'working');
                const isToday = r.date === todayStr;
                const isSun = r.weekday === '日';
                const isSat = r.weekday === '土';
                const textColor = isSun ? '#ef4444' : isSat ? '#3b82f6' : '#374151';
                const dotColor = isWorking ? '#f59e0b' : hasWork ? '#10b981' : 'transparent';
                const hasActs = (actsBySession[r.date] ?? []).length > 0;
                return (
                  <div key={r.date} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '4px 0', cursor: 'pointer' }}
                    onClick={() => setSelectedDate(r.date)}>
                    <div style={{
                      width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isToday ? '#0057d9' : selectedDate === r.date ? '#e0e7ff' : 'transparent',
                      border: hasWork && !isToday ? '1.5px solid ' + (isWorking ? '#f59e0b' : '#10b981') : 'none',
                    }}>
                      <span style={{ fontSize: 11, fontWeight: isToday ? 700 : hasWork ? 600 : 400, color: isToday ? '#fff' : textColor }}>{dayNum}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <div style={{ width: 4, height: 4, borderRadius: '50%', background: dotColor }} />
                      {hasActs && <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#a855f7' }} />}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* 凡例 */}
            <div style={{ display: 'flex', gap: 12, marginTop: 6, justifyContent: 'flex-end' }}>
              {(hasFeature('unified_work_types')
                ? [['#10b981', '出勤'], ['#f59e0b', '勤務中'], ['#a855f7', '業務の記録あり']]
                : [['#10b981', '出勤'], ['#f59e0b', '勤務中'], ['#a855f7', '臨時業務']]).map(([c, l]) => (
                <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#94a3b8' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: c }} />{l}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ─── 日別リスト ─────────────────────────────── */}
      <div style={{ padding: '10px 12px 8px' }}>
        {/* 未送信の勤怠修正（圏外で直したぶん）。電波が入ると自動で送られる */}
        {pendingEdits.length > 0 && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 14, marginBottom: 10, padding: '10px 14px' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#92400e' }}>
              未送信の修正 {pendingEdits.length}件
            </div>
            <div style={{ fontSize: 11.5, color: '#a16207', marginTop: 3, lineHeight: 1.6 }}>
              圏外のため端末に保存しています。電波が入ると自動で送信され、この表示は消えます。
            </div>
          </div>
        )}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>読み込み中...</div>
        ) : (
          records.map(r => {
            const isSun = r.weekday === '日', isSat = r.weekday === '土';
            const dateColor = isSun ? '#ef4444' : isSat ? '#3b82f6' : '#0f172a';
            // 通常勤怠の行（臨時業務専用の入れ物は勤怠として表示しない）
            const realSessions = r.sessions.filter(s => !s.isActivityOnly);
            const dayActs = actsBySession[r.date] ?? [];
            // 勤務なし＝通常勤怠も臨時業務も無い日は非表示（スッキリ化）
            if (realSessions.length === 0 && dayActs.length === 0) return null;

            return (
              <div key={r.date} style={{ background: '#fff', borderRadius: 14, marginBottom: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
                {/* 日付ヘッダー */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px 8px', borderBottom: '1px solid #f8fafc' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: isSun ? '#fef2f2' : isSat ? '#eff6ff' : '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 15, fontWeight: 800, color: dateColor, lineHeight: 1 }}>{r.date.slice(8)}</span>
                      <span style={{ fontSize: 9, color: dateColor, opacity: 0.7 }}>{r.weekday}</span>
                    </div>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>{r.date.slice(0, 7).replace('-', '年')}月</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {hasFeature('unified_work_types') ? (
                      /* 入口は1つだけ。作業内容と時刻を選ぶと、記録先はシステムが振り分ける */
                      <button onClick={() => setAddWorkDate(r.date)}
                        style={{ fontSize: 13, padding: '8px 14px', border: 'none', borderRadius: 10, background: '#0057d9', color: '#fff', cursor: 'pointer', fontWeight: 800 }}>
                        ＋ 業務を追加
                      </button>
                    ) : (<>
                      <button onClick={() => handleActivityForDate(r.date)}
                        style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #c4b5fd', borderRadius: 8, background: '#f5f3ff', color: '#7c3aed', cursor: 'pointer', fontWeight: 600 }}>
                        臨時
                      </button>
                      <button onClick={() => setNewEntryDate(r.date)}
                        style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc', color: '#64748b', cursor: 'pointer', fontWeight: 600 }}>
                        ＋ 追加
                      </button>
                    </>)}
                  </div>
                </div>

                {/* セッション */}
                <div style={{ padding: '8px 12px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {realSessions.map((s) => {
                    const isActive = s.status === 'working';
                    // 閾値は会社ごとに設定できるため、判定は共通処理に任せる
                    const alcoJudge = s.alcoholValue == null ? null : judgeAlcohol(s.alcoholValue);
                    const alcoInfo = alcoJudge == null ? null
                      : alcoJudge === 'normal'  ? { label: '正常',   color: '#059669', bg: '#f0fdf4' }
                      : alcoJudge === 'warning' ? { label: '要確認', color: '#d97706', bg: '#fffbeb' }
                      :                           { label: '超過',   color: '#dc2626', bg: '#fef2f2' };
                    return (
                      <div key={s.id} style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 12px', border: `1px solid ${isActive ? '#fde68a' : '#f1f5f9'}` }}>
                        {/* 文字の大きいスマホでは右のボタン群が収まらず「勤務中」が縦に潰れ「削除」が切れていた。
                            収まらないときはボタン群を下の行へ回す。 */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', rowGap: 8, columnGap: 8 }}>
                          <div style={{ minWidth: 0 }}>
                            {/* 時刻 */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{s.clockIn || '—'}</span>
                              <span style={{ fontSize: 12, color: '#cbd5e1' }}>→</span>
                              {isActive
                                ? <span style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b', background: '#fffbeb', padding: '2px 8px', borderRadius: 99, border: '1px solid #fde68a' }}>勤務中</span>
                                : <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>{s.clockOut || '—'}</span>
                              }
                            </div>
                            {/* 勤務時間バッジ */}
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 11, fontWeight: 600, color: '#0057d9', background: '#eff6ff', padding: '2px 8px', borderRadius: 99 }}>
                                {fmtMin(s.workMin)}
                              </span>
                              {(s.overMin ?? 0) > 0 && (
                                <span style={{ fontSize: 11, fontWeight: 600, color: '#dc2626', background: '#fef2f2', padding: '2px 8px', borderRadius: 99 }}>
                                  残業 {fmtMin(s.overMin)}
                                </span>
                              )}
                              {alcoInfo && (
                                <span style={{ fontSize: 11, fontWeight: 600, color: alcoInfo.color, background: alcoInfo.bg, padding: '2px 8px', borderRadius: 99 }}>
                                  🧪 {s.alcoholValue!.toFixed(3)} mg/L · {alcoInfo.label}
                                </span>
                              )}
                              {s.recordSource && s.recordSource !== 'gps_auto' && (
                                <span style={{ fontSize: 11, color: '#94a3b8', padding: '2px 0' }}>{SOURCE_LABEL[s.recordSource]}</span>
                              )}
                            </div>
                            {/* 何の作業だったか（臨時業務と同じ見せ方で表示） */}
                            <WorkTypeBadge workType={s.workType} types={vesselWorkTypes} />
                          </div>
                          <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center', marginLeft: 'auto' }}>
                            {s.pending && (
                              <span title="圏外のため端末に保存しています。電波が入ると自動で送信されます"
                                style={{ fontSize: 10.5, fontWeight: 800, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 999, padding: '3px 8px' }}>
                                未送信
                              </span>
                            )}
                            {!s.pending && (
                            <button onClick={() => {
                                const vid = (s as any).vessel_id || userVesselId;
                                setActivityModal({ sessionId: s.id, vesselId: vid, date: r.date });
                              }}
                              style={{ fontSize: 11, padding: '5px 10px', border: '1px solid #c4b5fd', borderRadius: 8, background: '#f5f3ff', color: '#7c3aed', cursor: 'pointer', fontWeight: 600 }}>
                              {hasFeature('unified_work_types') ? '作業を追加' : '臨時作業'}
                            </button>
                            )}
                            <button onClick={() => setEditTarget({ session: s, date: r.date })}
                              style={{ fontSize: 11, padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#374151', cursor: 'pointer', fontWeight: 600 }}>
                              修正
                            </button>
                            <button onClick={() => setDeleteConfirm({ id: s.id, date: r.date })}
                              style={{ fontSize: 11, padding: '5px 10px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}>
                              削除
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {/* 臨時業務も通常業務と同じ並びに表示する（日付単位。所属セッションが変わっても消えない） */}
                  {dayActs.map(log => (
                    <ActivityRow key={log.id} log={log}
                      onOpen={() => setActivityModal({ sessionId: log.work_session_id, vesselId: log.vessel_id || userVesselId, date: r.date })} />
                  ))}
                </div>
              </div>
            );
          })
        )}
        {/* 勤務なし日でも追加できるよう空きスペースに表示 */}
        {!loading && worked.length === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 16px 40px' }}>
            <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 16 }}>この月の記録はありません</div>
            {hasFeature('unified_work_types') && (
              <button onClick={() => setAddWorkDate(todayStr.slice(0, 7) === `${year}-${String(month).padStart(2, '0')}` ? todayStr : `${year}-${String(month).padStart(2, '0')}-01`)}
                style={{ padding: '15px 26px', border: 'none', borderRadius: 12, background: '#0057d9', color: '#fff', fontSize: 15, fontWeight: 900, cursor: 'pointer' }}>
                ＋ 業務を追加
              </button>
            )}
          </div>
        )}
      </div>
      </div>

      <BottomNav />

      {selectedDate && (() => {
        const dow = new Date(selectedDate).getDay();
        const rec = records.find(r => r.date === selectedDate) ?? { date: selectedDate, weekday: WEEKDAYS[dow] ?? '日', sessions: [] };
        return (
          <DayDetailSheet
            record={rec}
            userVesselId={userVesselId}
            workTypes={vesselWorkTypes}
            onClose={() => setSelectedDate(null)}
            onEdit={(session, date) => { setEditTarget({ session, date }); setSelectedDate(null); }}
            onDelete={(id, date) => { setDeleteConfirm({ id, date }); setSelectedDate(null); }}
            onNewEntry={(date) => { setNewEntryDate(date); setSelectedDate(null); }}
            onActivity={(sessionId, vesselId, date) => { setActivityModal({ sessionId, vesselId, date }); setSelectedDate(null); }}
            onActivityForDate={(date) => { handleActivityForDate(date); }}
            onAddWork={(date) => { setAddWorkDate(date); setSelectedDate(null); }}
            actsBySession={actsBySession}
          />
        );
      })()}

      {editTarget && (
        <CrewEditModal record={editTarget.session} date={editTarget.date}
          fallbackVesselId={userVesselId}
          onSave={() => { setEditTarget(null); triggerSync(); fetchData(); refreshPending(); setRequestsKey(k => k + 1); }}
          onClose={() => setEditTarget(null)} />
      )}
      {addWorkDate && (
        <AddWorkModal date={addWorkDate} vesselId={userVesselId}
          onClose={() => setAddWorkDate(null)}
          onSaved={() => { setAddWorkDate(null); triggerSync(); fetchData(); }} />
      )}
      {newEntryDate && (
        <NewEntryModal date={newEntryDate}
          autoActivity={newEntryAutoActivity}
          vesselId={userVesselId}
          onSave={() => { setNewEntryDate(null); setNewEntryAutoActivity(false); triggerSync(); fetchData(); refreshPending(); setRequestsKey(k => k + 1); }}
          onClose={() => { setNewEntryDate(null); setNewEntryAutoActivity(false); }}
          onSavedWithActivity={newEntryAutoActivity ? (id, vid, dt) => {
            setNewEntryDate(null);
            setNewEntryAutoActivity(false);
            triggerSync();
            fetchData();
            setActivityModal({ sessionId: id, vesselId: vid || userVesselId, date: dt });
          } : (id, vid, dt) => {
            fetchData();
            setActivityModal({ sessionId: id, vesselId: vid || userVesselId, date: dt });
          }}
        />
      )}
      {activityModal && (
        <ActivityLogModal
          sessionId={activityModal.sessionId}
          vesselId={activityModal.vesselId}
          sessionDate={activityModal.date}
          onClose={() => { setActivityModal(null); fetchData(); }}
          onSaved={() => { setActivityModal(null); fetchData(); }}
        />
      )}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxWidth: 480, boxShadow: '0 -8px 32px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>勤怠を削除</div>
            <div style={{ background: '#fef2f2', border: '1px solid #fecdd3', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#dc2626' }}>
              {deleteConfirm.date} の勤務記録を削除します
            </div>
            <p style={{ margin: '0 0 20px', fontSize: 12, color: '#94a3b8' }}>この操作は取り消せません。</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: 14, border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', cursor: 'pointer', fontSize: 14, color: '#475569', fontWeight: 600 }}>キャンセル</button>
              <button onClick={() => handleDelete(deleteConfirm.id, deleteConfirm.date)} style={{ flex: 2, padding: 14, border: 'none', borderRadius: 12, background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>削除する</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryPage;
